import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { BookOpen, Bot, CheckCircle2, Copy, Loader2, Pencil, Plus, Power, Search, Trash2, XCircle, Zap } from 'lucide-react';
import { Button } from '@components/Button';
import { ConfirmDialog, Dialog } from '@components/Dialog';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { Tabs } from '@components/Tabs';
import { MarkdownView } from '@components/help/MarkdownView';
import { DOCS } from '@/docs';
import { aiProviderGuideText } from '@/utils/integrationGuides';
import adminService from '@services/adminService';
import type { AIKind, AIModelInfo, AIProvider, AIProviderForm, AIPurpose, TestResult } from '@/types/admin';
import { AI_KIND_LABELS, AI_PURPOSE_LABELS } from '@/types/admin';
import { formatDateTimeBR, formatUSD } from '@/utils/formatters';

const PURPOSES: AIPurpose[] = ['vision', 'embedding', 'copywriting', 'landing', 'email'];

const PURPOSE_HELP: Record<AIPurpose, string> = {
  vision:
    'Lê as fotos e os quadros dos vídeos da Biblioteca e escreve descrição, tipo e tags. Precisa de um modelo com visão (ex.: claude-sonnet-5).',
  embedding:
    'Transforma descrições e buscas em vetores para a busca por significado. Use text-embedding-3-large (1024 dimensões): trocar o modelo exige reprocessar todas as mídias.',
  copywriting: 'Gera legendas, hashtags, ganchos e roteiros dos posts das redes sociais.',
  email:
    'Assistente do editor de e-mail marketing: altera o HTML das campanhas por conversa (tabelas e estilos inline). Em homologação é onde os e-mails são montados — mantenha um provedor ativo lá.',
  landing:
    'Assistente do editor de landing pages: altera o HTML das páginas por conversa. Modelo separado dos demais — um Sonnet dá conta de pedidos maiores; um Haiku é mais barato para ajustes simples de texto e cor.',
};

const emptyForm = (purpose: AIPurpose): AIProviderForm => ({
  name: '',
  purpose,
  kind: purpose === 'embedding' ? 'openai_compatible' : 'anthropic',
  base_url: purpose === 'embedding' ? 'https://api.openai.com/v1' : null,
  model: purpose === 'embedding' ? 'text-embedding-3-large' : '',
  api_key: '',
  params_json: purpose === 'embedding' ? '{"embedding_dim":1024}' : null,
  input_usd_per_mtok: null,
  output_usd_per_mtok: null,
  cache_read_usd_per_mtok: null,
  cache_write_usd_per_mtok: null,
});

const toForm = (p: AIProvider): AIProviderForm => ({
  name: p.name,
  purpose: p.purpose,
  kind: p.kind,
  base_url: p.base_url,
  model: p.model,
  api_key: p.api_key,
  params_json: p.params_json,
  input_usd_per_mtok: p.input_usd_per_mtok,
  output_usd_per_mtok: p.output_usd_per_mtok,
  cache_read_usd_per_mtok: p.cache_read_usd_per_mtok,
  cache_write_usd_per_mtok: p.cache_write_usd_per_mtok,
});

const num = (v: string): number | null => {
  const t = v.replace(',', '.').trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

const PriceInput: React.FC<{ label: string; value: number | null; onChange: (v: number | null) => void }> = ({ label, value, onChange }) => {
  const [text, setText] = useState(value === null ? '' : String(value).replace('.', ','));
  useEffect(() => setText(value === null ? '' : String(value).replace('.', ',')), [value]);
  return (
    <label className="block">
      <span className="text-label">{label}</span>
      <input
        className="input-field mt-1"
        inputMode="decimal"
        value={text}
        placeholder="—"
        onChange={(e) => setText(e.target.value)}
        onBlur={() => onChange(num(text))}
      />
    </label>
  );
};

/** Formulário de provedor (criar/editar), com "Buscar modelos" pela chave informada. */
const ProviderDialog: React.FC<{
  open: boolean;
  provider: AIProvider | null;
  purpose: AIPurpose;
  onClose: () => void;
  onSaved: () => void;
  onError: (e: unknown) => void;
}> = ({ open, provider, purpose, onClose, onSaved, onError }) => {
  const [form, setForm] = useState<AIProviderForm>(emptyForm(purpose));
  const [models, setModels] = useState<AIModelInfo[] | null>(null);
  const [loadingModels, setLoadingModels] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(provider ? toForm(provider) : emptyForm(purpose));
      setModels(null);
    }
  }, [open, provider, purpose]);

  const set = <K extends keyof AIProviderForm>(k: K, v: AIProviderForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  const fetchModels = async () => {
    setLoadingModels(true);
    try {
      setModels(await adminService.listModels(form, provider?.id));
    } catch (err) {
      onError(err);
    } finally {
      setLoadingModels(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminService.saveProvider({ ...form, base_url: form.base_url?.trim() || null, params_json: form.params_json?.trim() || null }, provider?.id);
      onSaved();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  const filtered = (models ?? []).filter((m) =>
    form.purpose === 'embedding' ? m.id.includes('embedding') : !m.id.includes('embedding') && !m.id.includes('whisper') && !m.id.includes('tts'),
  );

  return (
    <Dialog
      isOpen={open}
      onClose={onClose}
      size="lg"
      title={provider ? `Editar provedor — ${provider.name}` : `Novo provedor — ${AI_PURPOSE_LABELS[purpose]}`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={save} loading={saving} disabled={!form.name.trim() || !form.model.trim()}>
            Salvar
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block md:col-span-2">
          <span className="text-label">Nome</span>
          <input className="input-field mt-1" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Ex.: Claude Sonnet 5 — visão" />
        </label>
        <label className="block">
          <span className="text-label">Propósito</span>
          <select
            className="select-field mt-1"
            value={form.purpose}
            disabled={!!provider?.is_active}
            onChange={(e) => set('purpose', e.target.value as AIPurpose)}
          >
            {PURPOSES.map((p) => (
              <option key={p} value={p}>
                {AI_PURPOSE_LABELS[p]}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-label">Tipo</span>
          <select className="select-field mt-1" value={form.kind} onChange={(e) => set('kind', e.target.value as AIKind)}>
            {(Object.keys(AI_KIND_LABELS) as AIKind[]).map((k) => (
              <option key={k} value={k} disabled={form.purpose === 'embedding' && k === 'anthropic'}>
                {AI_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
        <label className="block md:col-span-2">
          <span className="text-label">Chave de API (em claro)</span>
          <input
            className="input-field mt-1 font-mono text-sm"
            value={form.api_key ?? ''}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => set('api_key', e.target.value)}
          />
        </label>
        <label className="block md:col-span-2">
          <span className="text-label">Base URL (vazio = padrão do fornecedor)</span>
          <input className="input-field mt-1 font-mono text-sm" value={form.base_url ?? ''} onChange={(e) => set('base_url', e.target.value)} />
        </label>
        <div className="md:col-span-2">
          <span className="text-label">Modelo</span>
          <div className="flex gap-2 mt-1">
            <input className="input-field font-mono text-sm" value={form.model} onChange={(e) => set('model', e.target.value)} list="ai-models" />
            <Button variant="outline" size="sm" icon={<Search />} loading={loadingModels} onClick={fetchModels} disabled={!form.api_key && !provider}>
              Buscar modelos
            </Button>
          </div>
          <datalist id="ai-models">
            {filtered.map((m) => (
              <option key={m.id} value={m.id}>
                {m.display_name ?? m.id}
              </option>
            ))}
          </datalist>
          {models && <span className="text-small text-slate-500">{filtered.length} modelo(s) disponível(is) para esta chave — digite ou escolha na lista.</span>}
        </div>
        <PriceInput label="Entrada (US$ / 1M tokens)" value={form.input_usd_per_mtok} onChange={(v) => set('input_usd_per_mtok', v)} />
        <PriceInput label="Saída (US$ / 1M tokens)" value={form.output_usd_per_mtok} onChange={(v) => set('output_usd_per_mtok', v)} />
        <PriceInput label="Leitura de cache (vazio = 10% da entrada)" value={form.cache_read_usd_per_mtok} onChange={(v) => set('cache_read_usd_per_mtok', v)} />
        <PriceInput label="Escrita de cache (vazio = 125% da entrada)" value={form.cache_write_usd_per_mtok} onChange={(v) => set('cache_write_usd_per_mtok', v)} />
        <label className="block md:col-span-2">
          <span className="text-label">Parâmetros (JSON)</span>
          <input
            className="input-field mt-1 font-mono text-sm"
            value={form.params_json ?? ''}
            placeholder='{"workspace_id":"wrkspc_..."}'
            onChange={(e) => set('params_json', e.target.value)}
          />
          <span className="text-small text-slate-500">
            workspace_id (Anthropic) direciona o consumo para o workspace do Marketing; embedding_dim deve ser 1024.
          </span>
        </label>
      </div>
    </Dialog>
  );
};

/** Admin → IA → Provedores: um provedor ativo por propósito, com troca justificada. */
export const AIProvidersView: React.FC = () => {
  const [providers, setProviders] = useState<AIProvider[]>([]);
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState<{ provider: AIProvider | null; purpose: AIPurpose } | null>(null);
  const [activating, setActivating] = useState<AIProvider | null>(null);
  const [note, setNote] = useState('');
  const [deleting, setDeleting] = useState<AIProvider | null>(null);
  const [busy, setBusy] = useState(false);
  const [tests, setTests] = useState<Record<number, TestResult | 'loading'>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setProviders(await adminService.listProviders());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const test = async (p: AIProvider) => {
    setTests((t) => ({ ...t, [p.id]: 'loading' }));
    try {
      const r = await adminService.testProvider(p.id);
      setTests((t) => ({ ...t, [p.id]: r }));
    } catch (err) {
      setTests((t) => {
        const n = { ...t };
        delete n[p.id];
        return n;
      });
      setError(err);
    }
  };

  const activate = async () => {
    if (!activating) return;
    setBusy(true);
    try {
      await adminService.activateProvider(activating.id, note);
      setActivating(null);
      setNote('');
      load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await adminService.deleteProvider(deleting.id);
      setDeleting(null);
      load();
    } catch (err) {
      setDeleting(null);
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  // Uma aba por propósito: os provedores e o passo a passo do fornecedor (src/docs/guias — fonte única).
  const renderPurpose = (purpose: AIPurpose) => {
    const list = providers.filter((p) => p.purpose === purpose);
    const guide = aiProviderGuideText(purpose, DOCS);
    return (
      <div className="space-y-4">
        <section key={purpose} className="card p-4 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h2 className="text-h4">{AI_PURPOSE_LABELS[purpose]}</h2>
              <p className="text-small text-slate-500 mt-1 max-w-3xl">{PURPOSE_HELP[purpose]}</p>
            </div>
            <Button size="sm" variant="outline" icon={<Plus />} onClick={() => setEditing({ provider: null, purpose })}>
              Novo provedor
            </Button>
          </div>
          {list.length === 0 && (
            <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
              Nenhum provedor cadastrado: as funções que dependem deste propósito ficam desligadas.
            </p>
          )}
          <div className="space-y-2">
            {list.map((p) => {
              const t = tests[p.id];
              return (
                <div
                  key={p.id}
                  className={clsx('border rounded-lg p-3 space-y-2', p.is_active ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200')}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-slate-800">{p.name}</span>
                    {p.is_active ? <span className="badge-success">Ativo</span> : <span className="badge-neutral">Inativo</span>}
                    <span className="text-small text-slate-500">
                      {AI_KIND_LABELS[p.kind]} · <span className="font-mono">{p.model}</span>
                    </span>
                    <div className="ml-auto flex items-center gap-1">
                      <Button size="sm" variant="outline" icon={<Zap />} loading={t === 'loading'} onClick={() => test(p)}>
                        Testar
                      </Button>
                      {!p.is_active && (
                        <Button size="sm" variant="outline" icon={<Power />} onClick={() => setActivating(p)}>
                          Ativar
                        </Button>
                      )}
                      <button className="btn-edit" title="Editar" onClick={() => setEditing({ provider: p, purpose })}>
                        <Pencil size={14} />
                      </button>
                      {!p.is_active && (
                        <button className="btn-delete" title="Excluir" onClick={() => setDeleting(p)}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-small text-slate-600">
                    <div className="flex items-start gap-1 min-w-0 md:col-span-2">
                      Chave:{' '}
                      <span className="font-mono break-all">{p.api_key || '—'}</span>
                      {p.api_key && (
                        <button title="Copiar" className="flex-shrink-0" onClick={() => navigator.clipboard?.writeText(p.api_key ?? '')}>
                          <Copy size={12} />
                        </button>
                      )}
                    </div>
                    <div>
                      Preço: {formatUSD(p.input_usd_per_mtok, 2)} entrada · {formatUSD(p.output_usd_per_mtok, 2)} saída (por 1M tokens)
                    </div>
                    {p.base_url && <div className="font-mono truncate">{p.base_url}</div>}
                    {p.params_json && <div className="font-mono truncate">{p.params_json}</div>}
                    {p.is_active && p.activated_at && (
                      <div className="md:col-span-2">
                        Ativado em {formatDateTimeBR(p.activated_at)} por {p.activated_by}
                        {p.activation_note && <> — “{p.activation_note}”</>}
                      </div>
                    )}
                    {p.source_crm_provider_id && <div className="md:col-span-2 text-slate-400">Copiado do provedor #{p.source_crm_provider_id} do CRM (carga inicial)</div>}
                  </div>
                  {t && t !== 'loading' && (
                    <div className={clsx('rounded px-2 py-1 text-sm flex items-start gap-2', t.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800')}>
                      {t.ok ? <CheckCircle2 size={14} className="mt-0.5" /> : <XCircle size={14} className="mt-0.5" />}
                      <span className="break-all">{t.message}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
        {guide && (
          <section className="card p-4 sm:p-5">
            <h2 className="text-h4 flex items-center gap-2 mb-3">
              <BookOpen size={18} className="text-slate-500" /> Passo a passo
            </h2>
            <MarkdownView source={guide} />
          </section>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-h3 flex items-center gap-2">
          <Bot size={20} className="text-slate-500" /> IA — Provedores
        </h1>
        <p className="text-small text-slate-500 mt-1">
          Modelos e chaves usados pelo Marketing, separados dos do CRM (orçamento próprio). Cada propósito tem um único
          provedor ativo; a troca vale na próxima chamada.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-slate-400" />
        </div>
      ) : (
        <Tabs
          tabs={PURPOSES.map((purpose) => ({ id: purpose, label: AI_PURPOSE_LABELS[purpose], content: renderPurpose(purpose) }))}
          defaultTab={PURPOSES.includes(params.get('tab') as AIPurpose) ? (params.get('tab') as string) : PURPOSES[0]}
          onTabChange={(id) => setParams({ tab: id }, { replace: true })}
        />
      )}

      <ProviderDialog
        open={!!editing}
        provider={editing?.provider ?? null}
        purpose={editing?.purpose ?? 'vision'}
        onClose={() => setEditing(null)}
        onError={setError}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />

      <Dialog
        isOpen={!!activating}
        onClose={() => setActivating(null)}
        title={`Ativar ${activating?.name ?? ''}`}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setActivating(null)}>
              Cancelar
            </Button>
            <Button onClick={activate} loading={busy} disabled={note.trim().length < 10}>
              Ativar
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600 mb-3">
          O provedor ativo de <strong>{activating ? AI_PURPOSE_LABELS[activating.purpose] : ''}</strong> será substituído. A troca
          muda custo e qualidade: teste antes e registre o motivo.
        </p>
        <label className="block">
          <span className="text-label">Justificativa (mínimo de 10 caracteres)</span>
          <textarea className="textarea-field mt-1" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
      </Dialog>

      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={remove}
        title="Excluir provedor"
        message={`Excluir "${deleting?.name}"? O histórico de consumo continua no painel.`}
        severity="danger"
        loading={busy}
      />
      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
